/**
 * Global "app refocus" signal.
 *
 * Fires the custom event `sakthi:refocus` when the tab becomes visible again
 * or the window regains focus. Pages can call `useRefocus(loadFn)` to
 * auto-refetch their data on return without stalling on the last polling
 * cycle.
 *
 * The dispatcher is installed exactly once, on first import. It is debounced
 * so back-to-back focus/visibility events don't hammer the API.
 */
import { useEffect } from "react";

const REFOCUS_EVENT = "sakthi:refocus";

let _installed = false;
let _lastDispatch = 0;
const REFOCUS_MIN_INTERVAL_MS = 2000;

function _dispatchRefocus() {
  const now = Date.now();
  if (now - _lastDispatch < REFOCUS_MIN_INTERVAL_MS) return;
  _lastDispatch = now;
  try {
    window.dispatchEvent(new CustomEvent(REFOCUS_EVENT));
  } catch { /* ignore */ }
}

export function installRefocusDispatcher() {
  if (_installed || typeof window === "undefined") return;
  _installed = true;
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") _dispatchRefocus();
  });
  window.addEventListener("focus", _dispatchRefocus);
  // Also refetch when the browser reports online again after a network dropout
  window.addEventListener("online", _dispatchRefocus);
}

// Install eagerly on module load so any page that imports the hook is covered.
installRefocusDispatcher();

/**
 * Attach `fn` to the global refocus event. `fn` is invoked whenever the tab
 * regains focus / visibility. `fn` should be stable (memoised) or the caller
 * accepts the extra re-registrations on every render.
 */
export function useRefocus(fn) {
  useEffect(() => {
    if (typeof fn !== "function") return;
    const handler = () => { try { fn(); } catch { /* ignore */ } };
    window.addEventListener(REFOCUS_EVENT, handler);
    return () => window.removeEventListener(REFOCUS_EVENT, handler);
  }, [fn]);
}
